import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription, type FeatureKey, FEATURE_MIN_PLAN, PLAN_LABELS, PLAN_PRICES } from "@/lib/subscriptionContext";

interface FeatureGateProps {
  feature: FeatureKey;
  children: ReactNode;
  /** When true, render an inline upgrade card instead of redirecting. */
  inline?: boolean;
  /** Optional friendly name for the upsell card. */
  featureName?: string;
}

/**
 * Route- and section-level access guard. Reads the user's plan from
 * Supabase via SubscriptionContext (no cached/default values) and either
 * renders the children or shows an upgrade prompt.
 */
export default function FeatureGate({ feature, children, inline = false, featureName }: FeatureGateProps) {
  const { hasFeature, loading, plan } = useSubscription();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasFeature(feature)) return <>{children}</>;

  const requiredPlan = FEATURE_MIN_PLAN[feature];
  const planLabel = PLAN_LABELS[requiredPlan];
  const price = PLAN_PRICES[requiredPlan]?.monthly ?? 0;

  const card = (
    <div className="max-w-md mx-auto bg-card rounded-xl border border-border shadow-card p-8 text-center">
      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
        <Lock className="w-5 h-5 text-primary" />
      </div>
      <h2 className="font-heading text-xl font-medium text-foreground mb-2">
        Available on {planLabel}
      </h2>
      <p className="text-sm text-muted-foreground mb-1">
        {featureName ? `${featureName} is part of the ${planLabel} plan.` : `This feature is part of the ${planLabel} plan.`}
      </p>
      <p className="text-xs text-muted-foreground mb-6">
        You're on <span className="capitalize font-medium text-foreground">{PLAN_LABELS[plan]}</span>
        {price > 0 ? ` — upgrade to ${planLabel} for $${price}/mo.` : "."}
      </p>
      <div className="flex gap-2 justify-center">
        <Button onClick={() => navigate("/pricing")} className="gap-2">
          Upgrade to {planLabel} <ArrowRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  if (inline) return card;

  // Full-page gate
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      {card}
    </div>
  );
}